import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SparaButton from '../SparaButton/SparaButton';
import { companyApi, fetcher } from '../../lib/api';
import styles from './AddCompanyFrom1819Modal.module.scss';

const normalizeKennitala = (value) => (value || '').replace(/\D+/g, '');

const LOGO_1819 = 'https://1819.is/img/1819logo-blue.svg';

const AddCompanyFrom1819Modal = ({ open, onClose, onCreated }) => {
    const { t } = useTranslation();
    const [kennitala, setKennitala] = useState('');
    const [editableName, setEditableName] = useState('');
    const [comment, setComment] = useState('');
    const [lookingUp, setLookingUp] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');
    const [notFound, setNotFound] = useState(false);
    const [nameOnlyMode, setNameOnlyMode] = useState(false);
    const [preview, setPreview] = useState(null);
    const busyRef = useRef(false);

    useEffect(() => {
        busyRef.current = lookingUp || creating;
    }, [lookingUp, creating]);

    useEffect(() => {
        if (!open) return undefined;

        setKennitala('');
        setEditableName('');
        setComment('');
        setLookingUp(false);
        setCreating(false);
        setError('');
        setNotFound(false);
        setNameOnlyMode(false);
        setPreview(null);

        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !busyRef.current) {
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const digits = normalizeKennitala(kennitala);
    const canSearch = digits.length === 10 && !lookingUp && !creating && !nameOnlyMode;
    const canCreateFrom1819 =
        editableName.trim().length > 0 &&
        digits.length === 10 &&
        !creating &&
        !lookingUp &&
        preview &&
        preview.company_from === '1819';
    const canCreateManual =
        editableName.trim().length > 0 &&
        !creating &&
        !lookingUp &&
        (nameOnlyMode || (notFound && digits.length === 10));

    const selectAndClose = (company) => {
        onCreated?.(company);
        onClose();
    };

    const enterNameOnlyMode = () => {
        setNameOnlyMode(true);
        setKennitala('');
        setError('');
        setNotFound(false);
        setPreview(null);
        setEditableName('');
        setComment('');
    };

    const exitNameOnlyMode = () => {
        setNameOnlyMode(false);
        setError('');
        setNotFound(false);
        setPreview(null);
        setEditableName('');
        setComment('');
    };

    const handleLookup = async (kennitalaOverride) => {
        const lookupDigits = normalizeKennitala(
            kennitalaOverride !== undefined ? kennitalaOverride : kennitala
        );
        if (lookupDigits.length !== 10 || lookingUp || creating || nameOnlyMode) return;

        setLookingUp(true);
        setError('');
        setNotFound(false);
        setPreview(null);
        setEditableName('');
        setComment('');

        try {
            const response = await fetcher(companyApi.getByKennitala(lookupDigits));
            const data = response?.results;

            if (!data) {
                setError(t('offer.add_company_not_found'));
                setNotFound(true);
                return;
            }

            if (data.company_from === 'Torgid') {
                if (data.disabled) {
                    setError(
                        t('offer.add_company_exists_disabled', {
                            name: data.name || lookupDigits,
                        })
                    );
                    return;
                }

                selectAndClose(data);
                return;
            }

            setPreview(data);
            setEditableName(data.name || '');
        } catch (err) {
            const status = err?.response?.status;
            const message = err?.response?.data?.message;

            if (status === 404) {
                setError(t('offer.add_company_not_found'));
                setNotFound(true);
            } else if (status === 422) {
                setError(message || t('offer.add_company_invalid_kennitala'));
            } else if (status === 502) {
                setError(t('offer.add_company_1819_unavailable'));
            } else {
                setError(message || t('offer.add_company_lookup_error'));
            }
        } finally {
            setLookingUp(false);
        }
    };

    const handleKennitalaChange = (event) => {
        const cleaned = normalizeKennitala(event.target.value).slice(0, 10);
        setKennitala(cleaned);

        if (cleaned.length < 10) {
            setError('');
            setNotFound(false);
            setPreview(null);
            setEditableName('');
            setComment('');
            return;
        }

        handleLookup(cleaned);
    };

    const handleCreateFrom1819 = async () => {
        if (!preview || !canCreateFrom1819) return;

        setCreating(true);
        setError('');

        try {
            const response = await fetcher(companyApi.createFrom1819, 'POST', {
                kennitala: digits,
                name: editableName.trim(),
                comment: comment.trim(),
            });
            const company = response?.results;
            if (!company?.id) {
                setError(t('offer.add_company_create_error'));
                return;
            }
            selectAndClose(company);
        } catch (err) {
            handleCreateError(err);
        } finally {
            setCreating(false);
        }
    };

    const handleCreateManual = async () => {
        if (!canCreateManual) return;

        setCreating(true);
        setError('');

        try {
            const payload = {
                name: editableName.trim(),
                comment: comment.trim(),
            };
            if (!nameOnlyMode && digits.length === 10) {
                payload.kennitala = digits;
            }

            const response = await fetcher(companyApi.quickCreate, 'POST', payload);
            const company = response?.results;
            if (!company?.id) {
                setError(t('offer.add_company_create_error'));
                return;
            }
            selectAndClose(company);
        } catch (err) {
            handleCreateError(err);
        } finally {
            setCreating(false);
        }
    };

    const handleCreateError = (err) => {
        const status = err?.response?.status;
        const data = err?.response?.data?.results;
        const message = err?.response?.data?.message;

        if (status === 409) {
            if (data?.disabled) {
                setError(
                    t('offer.add_company_exists_disabled', {
                        name: data?.name || digits,
                    })
                );
                setPreview(null);
                setNotFound(false);
            } else if (data?.id) {
                selectAndClose(data);
            } else {
                setError(
                    t('offer.add_company_exists', {
                        name: data?.name || digits,
                    })
                );
                setPreview(null);
                setNotFound(false);
            }
        } else if (status === 422) {
            setError(message || t('offer.add_company_invalid_kennitala'));
        } else if (status === 502) {
            setError(t('offer.add_company_1819_unavailable'));
        } else {
            setError(message || t('offer.add_company_create_error'));
        }
    };

    const showDetailsForm =
        nameOnlyMode || notFound || (preview && preview.company_from === '1819');

    const content = (
        <div
            className={styles.overlay}
            onClick={(event) => {
                if (event.target === event.currentTarget && !lookingUp && !creating) {
                    onClose();
                }
            }}
        >
            <div className={styles.dialog} role="dialog" aria-modal="true">
                <h3 className={styles.title}>{t('offer.add_company')}</h3>
                <p className={styles.subtitle}>
                    {nameOnlyMode
                        ? t('offer.add_company_name_only_help')
                        : t('offer.add_company_help')}
                </p>

                {!nameOnlyMode && (
                    <>
                        <div className={styles.searchRow}>
                            <div className={styles.field}>
                                <label className={styles.label} htmlFor="add-company-kennitala">
                                    {t('offer.add_company_kennitala')}
                                </label>
                                <input
                                    id="add-company-kennitala"
                                    className={styles.input}
                                    value={kennitala}
                                    onChange={handleKennitalaChange}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            handleLookup();
                                        }
                                    }}
                                    placeholder="0000000000"
                                    inputMode="numeric"
                                    disabled={lookingUp || creating}
                                    autoFocus
                                />
                            </div>
                            <div className={styles.searchButton}>
                                <SparaButton
                                    onClick={handleLookup}
                                    loading={lookingUp}
                                    disabled={!canSearch}
                                >
                                    {t('offer.add_company_search')}
                                </SparaButton>
                            </div>
                        </div>

                        <button
                            type="button"
                            className={styles.modeLink}
                            onClick={enterNameOnlyMode}
                            disabled={lookingUp || creating}
                        >
                            {t('offer.add_company_unknown_kennitala')}
                        </button>
                    </>
                )}

                {nameOnlyMode && (
                    <button
                        type="button"
                        className={styles.modeLink}
                        onClick={exitNameOnlyMode}
                        disabled={creating}
                    >
                        {t('offer.add_company_use_kennitala')}
                    </button>
                )}

                {error && <div className={`${styles.message} ${styles.messageError}`}>{error}</div>}

                {preview && preview.company_from === '1819' && (
                    <div className={styles.preview}>
                        <img
                            src={LOGO_1819}
                            alt="1819"
                            className={styles.brandLogo}
                        />
                        <div className={styles.previewHeader}>
                            {preview.logo ? (
                                <img src={preview.logo} alt="" className={styles.logo} />
                            ) : (
                                <span className={styles.logoPlaceholder} />
                            )}
                            <div className={styles.previewIdentity}>
                                <div className={styles.previewName}>{preview.name}</div>
                                <div className={styles.previewKennitala}>{preview.kennitala}</div>
                            </div>
                        </div>
                        <ul className={styles.previewList}>
                            {(preview.address || preview.city || preview.zip) && (
                                <li>
                                    <span className={styles.previewLabel}>
                                        {t('offer.add_company_address')}
                                    </span>
                                    <span>
                                        {[preview.address, preview.zip, preview.city]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </span>
                                </li>
                            )}
                            {!!preview.phones?.length && (
                                <li>
                                    <span className={styles.previewLabel}>
                                        {t('offer.add_company_phone')}
                                    </span>
                                    <span>{preview.phones.join(', ')}</span>
                                </li>
                            )}
                            {!!preview.emails?.length && (
                                <li>
                                    <span className={styles.previewLabel}>
                                        {t('offer.add_company_email')}
                                    </span>
                                    <span>{preview.emails.join(', ')}</span>
                                </li>
                            )}
                            {!!preview.websites?.length && (
                                <li>
                                    <span className={styles.previewLabel}>
                                        {t('offer.add_company_website')}
                                    </span>
                                    <span>{preview.websites.join(', ')}</span>
                                </li>
                            )}
                        </ul>
                    </div>
                )}

                {showDetailsForm && (
                    <div className={styles.detailsForm}>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="add-company-name">
                                {t('offer.add_company_name')}
                            </label>
                            <input
                                id="add-company-name"
                                className={styles.input}
                                value={editableName}
                                onChange={(event) => setEditableName(event.target.value)}
                                placeholder={t('offer.add_company_name_placeholder')}
                                disabled={creating}
                                autoFocus={nameOnlyMode || notFound}
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="add-company-comment">
                                {t('offer.add_company_comment')}
                            </label>
                            <textarea
                                id="add-company-comment"
                                className={styles.textarea}
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                placeholder={t('offer.add_company_comment_placeholder')}
                                disabled={creating}
                                rows={3}
                            />
                        </div>

                        <p className={styles.reviewNotice}>{t('offer.add_company_review_notice')}</p>
                    </div>
                )}

                <div className={styles.actions}>
                    <SparaButton
                        variant="outlined"
                        onClick={onClose}
                        disabled={lookingUp || creating}
                    >
                        {t('cancel')}
                    </SparaButton>
                    {preview && preview.company_from === '1819' && (
                        <SparaButton
                            onClick={handleCreateFrom1819}
                            loading={creating}
                            disabled={!canCreateFrom1819}
                        >
                            {t('offer.add_company_create')}
                        </SparaButton>
                    )}
                    {(notFound || nameOnlyMode) && (
                        <SparaButton
                            onClick={handleCreateManual}
                            loading={creating}
                            disabled={!canCreateManual}
                        >
                            {t('offer.add_company_save')}
                        </SparaButton>
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(content, document.body);
};

AddCompanyFrom1819Modal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func,
};

export default AddCompanyFrom1819Modal;
